import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import hardcodedConfig from '../firebase-applet-config.json';

// Load Firebase config from environment variables (production)
// Dynamically resolve auth domain to match canonical URL for OAuth redirects
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || hardcodedConfig.projectId;
const isProdDomain = typeof window !== 'undefined' && window.location.hostname.includes('pambo.biz');

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || hardcodedConfig.apiKey,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || (isProdDomain ? window.location.hostname : `${projectId}.firebaseapp.com`),
  projectId: projectId,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || hardcodedConfig.storageBucket,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || hardcodedConfig.messagingSenderId,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || hardcodedConfig.appId,
};

// Initialize Firebase SDK
const app = initializeApp(firebaseConfig);

// Initialize Firebase App Check for production abuse protection (only if key is provided)
const appCheckKey = import.meta.env.VITE_FIREBASE_APP_CHECK_KEY;
if (typeof window !== 'undefined' && appCheckKey) {
  // Use debug token in local development
  if (import.meta.env.DEV) {
    (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckKey),
      isTokenAutoRefreshEnabled: true,
    });
    console.log('🛡️ Firebase App Check initialized successfully.');
  } catch (err) {
    console.warn('⚠️ Firebase App Check initialization failed:', err);
  }
}

// Initialize Firestore with the specific database ID
export const db = getFirestore(app);

// Initialize Auth
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account',
});
export const facebookProvider = new FacebookAuthProvider();

// Initialize Storage
export const storage = getStorage(app);

const shouldFallbackToRedirect = (error: any) => {
  return (
    error?.code === 'auth/popup-blocked' ||
    error?.code === 'auth/popup-closed-by-user' ||
    error?.code === 'auth/cancelled-popup-request' ||
    error?.code === 'auth/operation-not-supported-in-this-environment'
  );
};

async function signInWithProvider(provider: GoogleAuthProvider | FacebookAuthProvider) {
  try {
    return await signInWithPopup(auth, provider);
  } catch (error: any) {
    if (shouldFallbackToRedirect(error)) {
      console.warn('Popup sign-in failed or blocked; falling back to redirect authentication.');
      return await signInWithRedirect(auth, provider);
    }
    throw error;
  }
}

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  };
}

export function handleFirestoreError(
  error: unknown,
  operationType: OperationType,
  path: string | null
) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo:
        auth.currentUser?.providerData.map((provider) => ({
          providerId: provider.providerId,
          displayName: provider.displayName,
          email: provider.email,
          photoUrl: provider.photoURL,
        })) || [],
    },
    operationType,
    path,
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

export const signInWithGoogle = async () => {
  try {
    return await signInWithProvider(googleProvider);
  } catch (error) {
    console.error('Error signing in with Google', error);
    throw error;
  }
};

export const signInWithFacebook = async () => {
  try {
    return await signInWithProvider(facebookProvider);
  } catch (error) {
    console.error('Error signing in with Facebook', error);
    throw error;
  }
};

export { getRedirectResult };

export const setupRecaptcha = async (containerId: string) => {
  try {
    if ((window as any).recaptchaVerifier) {
      (window as any).recaptchaVerifier.clear();
    }

    const verifier = new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
      callback: (_response: any) => {
        // reCAPTCHA solved, allow signInWithPhoneNumber.
      },
    });

    await verifier.render();
    (window as any).recaptchaVerifier = verifier;
    return verifier;
  } catch (error) {
    console.error('Error setting up reCAPTCHA', error);
    throw error;
  }
};

export const signInWithPhone = async (phoneNumber: string, appVerifier: any) => {
  try {
    return await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
  } catch (error) {
    console.error('Error signing in with Phone', error);
    throw error;
  }
};

export const loginWithEmail = async (email: string, password: string) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    console.error('Error signing in with Email', error);
    throw error;
  }
};

export const registerWithEmail = async (email: string, password: string, displayName: string) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    if (result.user) {
      await updateProfile(result.user, { displayName });
    }
    return result.user;
  } catch (error) {
    console.error('Error registering with Email', error);
    throw error;
  }
};

export const logout = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out', error);
    throw error;
  }
};
